import { Box, Stack, Typography } from '@mui/material';

import { BalanceSheetCellPosition } from '@/modules/balanceSheet/BalanceSheetTable/BalanceSheetTable.types';
import {
  BalanceSheetCell,
  BalanceSheetHeader,
  BalanceSheetRow,
} from '@/modules/balanceSheet/BalanceSheetTable/components';
import { COLORS } from '@/styles/tokens/colors';

export interface ApplicationRecordChangesTableColumn {
  headerName: string;
  align?: 'left' | 'right';
  minWidth?: number;
}

interface ApplicationRecordChangesTableProps {
  title: string;
  context?: string;
  columns: ApplicationRecordChangesTableColumn[];
  rows: string[][];
}

function getCellPosition(
  columnIndex: number,
  columnCount: number
): BalanceSheetCellPosition {
  if (columnIndex === 0) return 'start';
  if (columnIndex === columnCount - 1) return 'end';
  return 'middle';
}

export function ApplicationRecordChangesTable({
  title,
  context,
  columns,
  rows,
}: ApplicationRecordChangesTableProps) {
  if (!rows.length) return null;

  return (
    <Stack component="section" sx={{ overflowX: 'auto' }}>
      <Box
        aria-label={`${title} table`}
        role="table"
        sx={{
          display: 'grid',
          gridTemplateColumns: columns
            .map((column) => `minmax(${column.minWidth ?? 0}px, 1fr)`)
            .join(' '),
          minWidth: columns.reduce(
            (width, column) => width + (column.minWidth ?? 0),
            0
          ),
          width: '100%',
          '& [role="cell"] span': {
            overflow: 'visible',
            overflowWrap: 'anywhere',
            textOverflow: 'clip',
            whiteSpace: 'normal',
          },
        }}
      >
        <BalanceSheetRow enableHover={false} role="row">
          <Box
            role="cell"
            sx={{
              bgcolor: COLORS.GRAY[50],
              gridColumn: '1 / -1',
              '& > div': {
                borderBottomColor: COLORS.GRAY[200],
              },
            }}
          >
            <BalanceSheetHeader
              alignment="left"
              cellPosition="middle"
              density="dense"
              text={
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  gap={1}
                  sx={{ minWidth: 0, px: 0.75, width: '100%' }}
                >
                  <Typography
                    component="h3"
                    variant="eyebrow-xs"
                    sx={{ color: COLORS.TEXT.PRIMARY }}
                  >
                    {title}
                  </Typography>
                  {context && (
                    <Typography
                      variant="eyebrow-xs"
                      sx={{
                        color: COLORS.TEXT.PRIMARY,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {context}
                    </Typography>
                  )}
                </Stack>
              }
            />
          </Box>
        </BalanceSheetRow>
        <BalanceSheetRow enableHover={false} role="row">
          {columns.map((column, columnIndex) => (
            <BalanceSheetHeader
              key={column.headerName}
              alignment={column.align ?? 'left'}
              cellPosition={getCellPosition(columnIndex, columns.length)}
              density="dense"
              indentStartContent={false}
              role="columnheader"
              text={column.headerName}
            />
          ))}
        </BalanceSheetRow>
        {rows.map((row, rowIndex) => (
          <BalanceSheetRow key={rowIndex} enableHover={false} role="row">
            {columns.map((column, columnIndex) => (
              <BalanceSheetCell
                key={columnIndex}
                alignment={column.align ?? 'left'}
                cellPosition={getCellPosition(columnIndex, columns.length)}
                cellStyle="default-primary"
                density="dense"
                indentStartContent={false}
                lineOne={row[columnIndex] ?? '—'}
                role="cell"
              />
            ))}
          </BalanceSheetRow>
        ))}
      </Box>
    </Stack>
  );
}
